import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { useEffect, useState } from "react";
import { Select } from "antd";
import { getAllStudents } from "../../api/etudiants";
import { getStudentParcours } from "../../api/settings";
import { checkStudentEligibility } from "../../api/notes";
import { createDecisionStudent } from "../../api/diplome";
import { AxiosError } from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

type DecisionRefreshScope = "all" | "students" | "validations" | "status";
type Props = {
  show: boolean;
  idDecision: string;
  onClose: () => void;
  onSaved?: (shouldReload: boolean, scopes: DecisionRefreshScope[]) => void;
};

type StudentCycle = "Licence" | "Master";

const createSchema = z.object({
  id_decision: z.string().uuid("ID de décision invalide"),
  id_parcoursStudent: z.string().uuid("ID de parcours d'étudiant invalide"),
  id_studentProfil: z.string().uuid("ID de profil d'étudiant invalide"),
});

type FormFields = z.infer<typeof createSchema>;

const AddStudentToDecisionModal = ({
  show,
  idDecision,
  onClose,
  onSaved,
}: Props) => {
  const { selectedYear } = useAcademicYear();

  const [allStudents, setAllStudents] = useState<any[]>([]);
  const [studentEligibility, setStudentEligibility] = useState<any | null>(
    null,
  );
  const [allStudentParcours, setAllStudentParcours] = useState<any[]>([]);
  const [selectedStudentParcours, setSelectedStudentParcours] = useState<
    any | null
  >(null);
  const [selectedStudent, setSelectedStudent] = useState<any | null>(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);

  const MySwal = withReactContent(Swal);

  const defaultFormValues: FormFields = {
    id_decision: idDecision,
    id_parcoursStudent: "",
    id_studentProfil: "",
  };

  const {
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: defaultFormValues,
  });

  const resetDecisionForm = () => {
    reset(defaultFormValues);
    setSelectedStudent(null);
    setAllStudentParcours([]);
    setSelectedStudentParcours(null);
    setStudentEligibility(null);
    setIsCheckingEligibility(false);
  };

  const handleClose = () => {
    resetDecisionForm();
    onClose();
  };

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    if (!studentEligibility?.est_eligible) {
      const result = await MySwal.fire({
        title: "Étudiant non éligible",
        text: "L'étudiant sélectionné n'est pas éligible pour cette décision, voulez-vous tout de même l'ajouter ?",
        showCancelButton: true,
        confirmButtonText: "Ajouter quand même",
        cancelButtonText: "Annuler",
      });

      if (!result.isConfirmed) return;

      MySwal.fire({
        title: "Ajout en cours...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => MySwal.showLoading(),
      });

      try {
        await createDecisionStudent(
          data,
          buildPermissionContext(Resource.DIPLOME, Action.CREATE),
        );
        MySwal.close();
        toast.success("Étudiant ajouté à la décision.");
        if (onSaved) onSaved(false, ["students"]);
        handleClose();
      } catch (error) {
        const err = error as AxiosError<any>;
        MySwal.fire({
          title: "Erreur",
          text:
            err.response?.data?.message ||
            err.response?.data?.error ||
            "Une erreur est survenue, réessayez plus tard",
          icon: "error",
          confirmButtonText: "OK",
        });
      }

      return;
    }

    try {
      await createDecisionStudent(
        data,
        buildPermissionContext(Resource.DIPLOME, Action.CREATE),
      );

      toast.success("Étudiant ajouté à la décision.");
      if (onSaved) onSaved(false, ["students"]);
      handleClose();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produite, réessayez plus tard",
      );
    }
  };

  const fetchStudents = async () => {
    if (!selectedYear?.id) return;

    try {
      const response = await getAllStudents(
        buildPermissionContext(Resource.DIPLOME, Action.CREATE),
      );
      // console.log("students: ", response.data);
      setAllStudents(response.data || []);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      setAllStudents([]);
    }
  };

  const fetchStudentParcours = async () => {
    if (!selectedYear?.id) return;

    try {
      const response = await getStudentParcours(
        selectedStudent?.id,
        buildPermissionContext(Resource.DIPLOME, Action.CREATE),
      );
      // console.log("student parcours: ", response.data);
      setAllStudentParcours(response.data.ParcoursStudents || []);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      setAllStudentParcours([]);
    }
  };

  const fetchStudentEligibility = async () => {
    if (!selectedStudent?.id || !selectedStudentParcours?.id) return;

    try {
      setIsCheckingEligibility(true);

      const response = await checkStudentEligibility(
        {
          id_studentProfil: selectedStudent?.id,
          id_parcours: selectedStudentParcours?.Parcour?.id,
        },
        buildPermissionContext(Resource.DIPLOME, Action.CREATE),
      );
      // console.log("student eligibility: ", response);
      setStudentEligibility({
        ...response,
        cycleLabel: selectedStudentParcours?.Parcour?.Cycle?.label || "-",
        parcoursTitle: selectedStudentParcours?.Parcour?.title || "-",
      });
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      setStudentEligibility(null);
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const getMentionFromAverage = (average: number) => {
    if (average >= 18) return "Excellent";
    if (average >= 16) return "Très bien";
    if (average >= 14) return "Bien";
    if (average >= 12) return "Assez bien";
    if (average >= 10) return "Passable";
    return "Ajourné";
  };

  const getProgressColor = (average: number) => {
    if (average >= 18) return "#ae0f69";
    if (average >= 16) return "#0fae46";
    if (average >= 14) return "#2563eb";
    if (average >= 12) return "#dfa424";
    if (average >= 10) return "#ff7931";
    return "#e42c2c";
  };

  useEffect(() => {
    if (show) {
      reset({
        id_decision: idDecision,
        id_studentProfil: selectedStudent?.id || "",
        id_parcoursStudent: selectedStudentParcours?.id || "",
      });
    }
  }, [show, idDecision, selectedStudent, selectedStudentParcours, reset]);

  useEffect(() => {
    if (!show) return;
    fetchStudents();
  }, [show, selectedYear?.id]);

  useEffect(() => {
    if (!selectedStudent?.id || !show || !idDecision) return;
    fetchStudentParcours();
  }, [selectedStudent, show, idDecision]);

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Ajouter un étudiant à la décision</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errors.root && (
          <div className="alert alert-danger rounded-pill alert-dismissible fade show">
            {errors.root.message}
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => clearErrors("root")}
            >
              <i className="fas fa-xmark" />
            </button>
          </div>
        )}

        <form
          id="decision-form"
          className="needs-validation"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="row ">
            <div className="col-12">
              <div className="card shadow-sm border-dark rounded">
                <div className="card-header bg-light d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Étudiants concernés</h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => {
                      if (
                        !selectedStudent?.id ||
                        !selectedStudentParcours?.id
                      ) {
                        toast.error(
                          "Veuillez d'abord sélectionner un étudiant et son parcours pour vérifier son éligibilité",
                        );
                        return;
                      } else {
                        fetchStudentEligibility();
                      }
                    }}
                  >
                    <i className="ti ti-refresh me-1" />
                    Vérifier l'éligibilité de l'étudiant
                  </button>
                </div>
                <div className="card-body pb-0 pt-1 row">
                  <div className="col-6">
                    <div className="mb-2">
                      <label className="form-label mb-1">Étudiant</label>
                      <Select
                        showSearch
                        allowClear
                        size="large"
                        optionFilterProp="label"
                        removeIcon={<span style={{ fontSize: 12 }}>×</span>}
                        style={{ width: "100%" }}
                        getPopupContainer={(trigger) =>
                          trigger.parentElement as HTMLElement
                        }
                        dropdownStyle={{ zIndex: 2000 }}
                        placeholder="Sélectionner les étudiants concernés par cette décision"
                        value={selectedStudent?.id}
                        onChange={(id) => {
                          const selected =
                            allStudents.find((student) => student.id === id) ||
                            null;
                          setSelectedStudent(selected);
                          setSelectedStudentParcours(null);
                          setStudentEligibility(null);
                          setAllStudentParcours([]);
                        }}
                        onClear={() => {
                          setSelectedStudent(null);
                          setSelectedStudentParcours(null);
                          setStudentEligibility(null);
                          setAllStudentParcours([]);
                        }}
                      >
                        {allStudents.map((student: any) => (
                          <Select.Option
                            key={student.id}
                            value={student.id}
                            label={`${student.matricule || "-"} - ${student.firstname || ""} ${student.lastname || ""}`}
                          >
                            {student.matricule || "-"} - {student.firstname}{" "}
                            {student.lastname}
                          </Select.Option>
                        ))}
                      </Select>
                      {errors.id_studentProfil && (
                        <div className="invalid-feedback d-block">
                          {errors.id_studentProfil.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-6">
                    <div className="mb-2">
                      <label className="form-label mb-1">
                        Cycle - Parcours{" "}
                      </label>
                      <Select
                        allowClear
                        size="large"
                        optionFilterProp="label"
                        removeIcon={<span style={{ fontSize: 12 }}>×</span>}
                        style={{ width: "100%" }}
                        getPopupContainer={(trigger) =>
                          trigger.parentElement as HTMLElement
                        }
                        dropdownStyle={{ zIndex: 2000 }}
                        placeholder="Sélectionner le parcours et le cycle de l'étudiant"
                        value={selectedStudentParcours?.Parcour?.id}
                        onChange={(id) => {
                          const selected =
                            allStudentParcours.find(
                              (parcours) => parcours?.Parcour?.id === id,
                            ) || null;
                          setSelectedStudentParcours(selected);
                        }}
                        onClear={() => {
                          setSelectedStudentParcours(null);
                          setStudentEligibility(null);
                        }}
                      >
                        {allStudentParcours.map((parcours: any) => (
                          <Select.Option
                            key={parcours.Parcour?.id}
                            value={parcours.Parcour?.id}
                            label={`${parcours.Parcour?.Cycle?.label || "-"} - ${parcours.Parcour?.title || ""}`}
                          >
                            {parcours.Parcour?.Cycle?.label || "-"} -{" "}
                            {parcours.Parcour?.title || ""}
                          </Select.Option>
                        ))}
                      </Select>

                      {errors.id_parcoursStudent && (
                        <div className="invalid-feedback d-block">
                          {errors.id_parcoursStudent.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {isCheckingEligibility ? (
              <div className="col-12">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-body text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{ width: "2.5rem", height: "2.5rem" }}
                    />
                    <div className="mt-3 text-muted">
                      Vérification de l'éligibilité...
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              studentEligibility && (
                <div className="col-12">
                  <div className="card shadow-sm border-dark rounded">
                    <div className="card-header bg-light d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <div>
                        <h5 className="mb-1 text-dark">
                          Parcours {studentEligibility?.cycleLabel} en{" "}
                          {studentEligibility?.parcoursTitle} (
                          {
                            studentEligibility?.data?.classes?.[0]?.annee?.split(
                              "-",
                            )?.[0]
                          }{" "}
                          -{" "}
                          {
                            studentEligibility?.data?.classes?.[
                              studentEligibility?.data?.classes?.length - 1
                            ]?.annee?.split("-")?.[1]
                          }
                          )
                        </h5>
                        <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                          Synthèse des performances académiques par niveau et
                          session.
                        </p>
                      </div>
                      <span
                        className="badge rounded-pill"
                        style={{
                          background: "#e2e8f0",
                          color: "#0f172a",
                          border: "1px solid #cbd5e1",
                          padding: "8px 12px",
                          fontSize: 12,
                        }}
                      >
                        {studentEligibility?.data?.classes.length} classe
                        {studentEligibility?.data?.classes.length > 1
                          ? "s"
                          : ""}
                      </span>
                    </div>
                    <div
                      className="card-body p-3"
                      style={{ background: "#f8fafc" }}
                    >
                      <div className="table-responsive">
                        <table
                          className="table table-bordered table-hover align-middle mb-0"
                          style={{
                            background: "#fff",
                            borderColor: "#cbd5e1",
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  background: "#e2e8f0",
                                  color: "#0f172a",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  borderColor: "#cbd5e1",
                                }}
                              >
                                Classe
                              </th>
                              <th
                                style={{
                                  background: "#e2e8f0",
                                  color: "#0f172a",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  borderColor: "#cbd5e1",
                                }}
                              >
                                Moyenne
                              </th>
                              <th
                                style={{
                                  background: "#e2e8f0",
                                  color: "#0f172a",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  borderColor: "#cbd5e1",
                                }}
                              >
                                Matière en session
                              </th>
                              <th
                                style={{
                                  background: "#e2e8f0",
                                  color: "#0f172a",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  borderColor: "#cbd5e1",
                                }}
                              >
                                Scolarité
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {studentEligibility?.data?.classes.map(
                              (item: any, index: number) => {
                                const isExternal = item?.type === "externe";
                                const average = isExternal
                                  ? item?.moyenne
                                  : item?.moyenneLevel;
                                const universityName =
                                  (isExternal && item?.university) || "-";
                                const failedSubjectsCount = isExternal
                                  ? 0
                                  : item?.matieres_non_validees?.length || 0;
                                const paymentStatus = isExternal
                                  ? "A jour"
                                  : item?.paiement?.statut;

                                return (
                                  <tr
                                    key={index}
                                    style={{ borderColor: "#dbe4ee" }}
                                  >
                                    <td>
                                      <div className="fw-semibold text-dark">
                                        {isExternal
                                          ? `${item?.label_niveau || "--"} ${item?.classe || "--"} (Externe)`
                                          : `${item?.level?.abbreviation || "--"} ${item?.classe?.label || "--"}`}
                                      </div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 12 }}
                                      >
                                        {item?.annee}{" "}
                                        {isExternal &&
                                          universityName &&
                                          `- ${universityName}`}
                                      </div>
                                    </td>
                                    <td style={{ minWidth: 180 }}>
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="fw-semibold text-dark">
                                          {Number(average || 0).toFixed(1)} / 20
                                        </span>
                                        <span
                                          className="fw-medium text-dark"
                                          style={{
                                            fontSize: 12,
                                            color: getProgressColor(average),
                                          }}
                                        >
                                          {getMentionFromAverage(average)}
                                          {/* {Math.round((item.moyenne / 20) * 100)}% */}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          height: 10,
                                          borderRadius: 999,
                                          background: "#e9ecef",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: `${Math.max(0, Math.min((average / 20) * 100, 100))}%`,
                                            height: "100%",
                                            borderRadius: 999,
                                            background:
                                              getProgressColor(average),
                                          }}
                                        />
                                      </div>
                                    </td>
                                    <td>
                                      <span className="fw-medium text-dark">
                                        {failedSubjectsCount} matière
                                        {failedSubjectsCount > 1 ? "s" : ""}
                                      </span>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 12 }}
                                      >
                                        {failedSubjectsCount > 0
                                          ? "À reprendre en session"
                                          : "Aucune matière en session"}
                                      </div>
                                    </td>
                                    <td>
                                      <span
                                        className={`badge ${
                                          paymentStatus === "A jour"
                                            ? "bg-success "
                                            : paymentStatus
                                              ? "bg-danger"
                                              : "bg-secondary"
                                        }`}
                                      >
                                        {paymentStatus || "Statut inconnu"}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="row pt-3 pb-3 g-3">
                        <div className="col-md-4">
                          <div
                            className="h-100 rounded-3 p-3 shadow-sm"
                            style={{
                              background: "#ecfdf3",
                              border: "1px solid #bbf7d0",
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="text-muted small fw-semibold text-uppercase fw-bold fs-3">
                                {studentEligibility?.data.moyenne_cycle} / 20
                              </span>
                              <span
                                className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                  width: 42,
                                  height: 42,
                                  background: "#16a34a",
                                  color: "#fff",
                                  boxShadow:
                                    "0 10px 18px rgba(22, 163, 74, 0.22)",
                                }}
                              >
                                <i className="ti ti-chart-arcs-3" />
                              </span>
                            </div>
                            <small className="text-muted d-block">
                              Moyenne Générale
                            </small>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div
                            className="h-100 rounded-3 p-3 shadow-sm"
                            style={{
                              background: "#fff7ed",
                              border: "1px solid #fdba74",
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-between ">
                              <span
                                className="text-muted small fw-semibold text-uppercase fs-3 fw-bold text-warning"
                                style={{ letterSpacing: "0.03em" }}
                              >
                                {studentEligibility?.data?.classes?.reduce(
                                  (total: number, item: any) =>
                                    total +
                                    (item?.matieres_non_validees?.length || 0),
                                  0,
                                ) || 0}
                              </span>
                              <span
                                className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                  width: 42,
                                  height: 42,
                                  background: "#f59e0b",
                                  color: "#fff",
                                  boxShadow:
                                    "0 10px 18px rgba(245, 158, 11, 0.22)",
                                }}
                              >
                                <i className="ti ti-refresh-alert" />
                              </span>
                            </div>
                            <small className="text-muted d-block">
                              Matières en session
                            </small>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div
                            className="h-100 rounded-3 p-3 shadow-sm"
                            style={{
                              background: "#f5f3ff",
                              border: "1px solid #c4b5fd",
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <span
                                className="text-muted small fw-semibold fs-4 fw-bold text-primary"
                                style={{ letterSpacing: "0.03em" }}
                              >
                                {studentEligibility?.data.mention}
                              </span>
                              <span
                                className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                  width: 42,
                                  height: 42,
                                  background: "#7c3aed",
                                  color: "#fff",
                                  boxShadow:
                                    "0 10px 18px rgba(124, 58, 237, 0.22)",
                                }}
                              >
                                <i className="ti ti-award" />
                              </span>
                            </div>
                            {/* <div className="fs-4 fw-bold text-primary">{studentEligibility?.data.mention}</div> */}
                            <small className="text-muted d-block">
                              Mention globale
                            </small>
                          </div>
                        </div>
                      </div>
                      {studentEligibility?.est_eligible === true ? (
                        <div
                          className="w-100 rounded-3 p-3 d-flex align-items-center justify-content-between"
                          style={{
                            background:
                              "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.12)",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="me-3 d-inline-flex align-items-center justify-content-center rounded-circle"
                              style={{
                                width: 42,
                                height: 42,
                                background: "rgba(255,255,255,0.18)",
                              }}
                            >
                              <i className="ti ti-circle-check fs-4" />
                            </div>
                            <div>
                              <div className="fw-bold ">Éligible</div>
                              <div style={{ fontSize: 13, opacity: 0.9 }}>
                                L’étudiant remplit les conditions de diplomation
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="w-100 rounded-3 p-3 d-flex align-items-center justify-content-between"
                          style={{
                            background:
                              "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.12)",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="me-3 d-inline-flex align-items-center justify-content-center rounded-circle"
                              style={{
                                width: 42,
                                height: 42,
                                background: "rgba(255,255,255,0.18)",
                              }}
                            >
                              <i className="ti ti-circle-x fs-4" />
                            </div>
                            <div>
                              <div className="fw-bold ">Inéligible</div>
                              <div style={{ fontSize: 13, opacity: 0.92 }}>
                                {studentEligibility?.data?.motif.map(
                                  (m: any) => (
                                    <span key={m} className="ms-2">
                                      {m} •
                                    </span>
                                  ),
                                ) ||
                                  "L’étudiant ne remplit pas les conditions de diplomation"}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-light me-3"
          onClick={handleClose}
        >
          Annuler
        </button>

        {isSubmitting ? (
          <button className="btn btn-primary-light" type="button" disabled>
            <span
              className="spinner-border spinner-border-sm align-middle me-1"
              role="status"
              aria-hidden="true"
            />
            Chargement...
          </button>
        ) : (
          <button
            disabled={
              selectedStudentParcours === null ||
              selectedStudent === null ||
              studentEligibility === null
            }
            type="submit"
            form="decision-form"
            className="btn btn-primary"
          >
            Ajouter à la décision
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default withPermission(
  AddStudentToDecisionModal,
  [buildPermission(Resource.DIPLOME, Action.CREATE)],
  "masquer",
);